import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Wallet, TrendingUp, Sparkles, Loader2, CheckCircle2, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";

export default function WalletPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [buying, setBuying] = useState(null); // pack key while purchasing
  const [crediting, setCrediting] = useState(false);

  const { data: balance } = useQuery({
    queryKey: ["wallet-balance"],
    queryFn: () => api.get("/wallet/balance").then(r => r.data),
    refetchInterval: 15000,
  });
  const { data: packsData } = useQuery({
    queryKey: ["wallet-packs"],
    queryFn: () => api.get("/wallet/packs").then(r => r.data),
  });
  const { data: ledgerData } = useQuery({
    queryKey: ["wallet-ledger"],
    queryFn: () => api.get("/wallet/ledger").then(r => r.data),
    refetchInterval: 30000,
  });

  const buy = async (packKey) => {
    if (!window.Razorpay) {
      toast.error("Checkout.js didn't load — refresh the page and try again");
      return;
    }
    setBuying(packKey);
    try {
      // 1) Create a real Razorpay order on our backend
      const { data: order } = await api.post("/wallet/topup/order", { pack: packKey });
      if (!order.key_id) {
        toast.error("Razorpay not configured on the server yet");
        setBuying(null);
        return;
      }
      // 2) Open Razorpay Checkout.js
      await new Promise((resolve) => {
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          order_id: order.order_id,
          name: "AIMOS by Neuronex.ai",
          description: `${order.tokens.toLocaleString()} tokens · ${order.pack}`,
          prefill: {
            name: user?.name || "",
            email: user?.email || "",
          },
          theme: { color: "#2563EB" },
          handler: async (resp) => {
            try {
              const { data: verify } = await api.post("/wallet/topup/verify", {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              toast.success(`+${verify.credited_tokens.toLocaleString()} tokens · balance ${verify.new_balance.toLocaleString()}`);
              qc.invalidateQueries({ queryKey: ["wallet-balance"] });
              qc.invalidateQueries({ queryKey: ["wallet-ledger"] });
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Signature verification failed");
            } finally {
              resolve();
            }
          },
          modal: {
            ondismiss: () => {
              toast.message("Payment cancelled");
              resolve();
            },
          },
        });
        rzp.on("payment.failed", (resp) => {
          toast.error(resp?.error?.description || "Payment failed");
          resolve();
        });
        rzp.open();
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Top-up failed to start");
    } finally {
      setBuying(null);
    }
  };

  const adminCredit = async (tokens) => {
    const confirmMsg = `Credit ${tokens.toLocaleString()} tokens to the current workspace? (Super-admin only — ledger entry recorded.)`;
    if (!window.confirm(confirmMsg)) return;
    setCrediting(true);
    try {
      const { data } = await api.post("/wallet/admin/credit", { tokens, note: `Super-admin manual credit · ${tokens}` });
      toast.success(`+${tokens} tokens · new balance ${data.new_balance.toLocaleString()}`);
      qc.invalidateQueries({ queryKey: ["wallet-balance"] });
      qc.invalidateQueries({ queryKey: ["wallet-ledger"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Credit failed");
    } finally {
      setCrediting(false);
    }
  };

  const packs = packsData?.packs || [];
  const ledger = ledgerData?.entries || [];

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="border-b border-slate-200 px-8 py-5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">// Wallet · Neuronex tokens</div>
        <h1 className="font-display font-bold text-2xl mt-1">Fuel your studio.</h1>
      </div>

      <div className="p-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Balance + costs */}
        <div className="lg:col-span-4 space-y-4">
          <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50 to-sky-50 p-6" data-testid="wallet-balance-card">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-blue-700">
              <Wallet className="w-3.5 h-3.5" /> // Available balance
            </div>
            <div className="mt-3 font-display font-black text-5xl text-blue-900" data-testid="wallet-balance-value">
              {balance?.balance ?? "—"}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">
              {balance?.tokens_used_total ?? 0} tokens used lifetime
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 p-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">// Render cost</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">15–30s reel</span><span className="font-mono text-blue-600 font-bold">50 tokens</span></div>
              <div className="flex justify-between"><span className="text-slate-600">31–60s reel</span><span className="font-mono text-blue-600 font-bold">75 tokens</span></div>
              <div className="flex justify-between"><span className="text-slate-600">61–120s film</span><span className="font-mono text-blue-600 font-bold">100 tokens</span></div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 font-mono text-[9px] uppercase tracking-widest text-slate-400">
              Charge is at render start. Refunded automatically if render fails.
            </div>
          </div>

          {user?.role === "super_admin" && (
            <div data-testid="admin-credit-card" className="rounded-xl border border-slate-900 bg-slate-900 text-white p-5">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-slate-300 mb-3">
                <Shield className="w-3.5 h-3.5" /> // Super-admin credit
              </div>
              <div className="text-xs text-slate-400 mb-3">
                Instantly credit this workspace. Works in preview AND production. Logged as `admin_credit` in the ledger.
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[1000, 10000, 100000].map((n) => (
                  <Button
                    key={n}
                    data-testid={`admin-credit-${n}`}
                    onClick={() => adminCredit(n)}
                    disabled={crediting}
                    className="h-9 bg-white text-slate-900 hover:bg-slate-100 rounded-md font-mono text-xs"
                  >
                    {crediting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `+${n.toLocaleString()}`}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Packs */}
        <div className="lg:col-span-8 space-y-4">
          <div className="flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">// Top up · Razorpay</div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-emerald-600 font-bold">
              LIVE · Razorpay Checkout
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {packs.map((p) => {
              const perToken = (p.price_inr / p.tokens).toFixed(2);
              const isPopular = p.key === "growth";
              return (
                <div
                  key={p.key}
                  data-testid={`pack-${p.key}`}
                  className={`relative rounded-xl border-2 p-6 transition-all hover:shadow-lg ${
                    isPopular ? "border-blue-600 bg-blue-50/40" : "border-slate-200 bg-white"
                  }`}
                >
                  {isPopular && (
                    <span className="absolute -top-2 right-4 bg-blue-600 text-white text-[9px] uppercase font-mono tracking-widest px-2 py-0.5 rounded-full font-bold">
                      Most popular
                    </span>
                  )}
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{p.label}</div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="font-display font-black text-3xl text-slate-900">{p.tokens.toLocaleString()}</span>
                    <span className="text-sm text-slate-500">tokens</span>
                  </div>
                  <div className="mt-1 font-display font-bold text-xl text-blue-700">₹{p.price_inr.toLocaleString()}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mt-1">₹{perToken}/token</div>
                  <Button
                    data-testid={`buy-${p.key}`}
                    onClick={() => buy(p.key)}
                    disabled={!!buying}
                    className={`w-full mt-4 h-11 rounded-lg font-medium ${
                      isPopular ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"
                    }`}
                  >
                    {buying === p.key
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing…</>
                      : <><Sparkles className="w-4 h-4 mr-2" />Buy · ₹{p.price_inr}</>
                    }
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Ledger */}
          <div className="rounded-xl border border-slate-200">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-slate-500" />
              <div className="font-display font-bold text-sm">Activity</div>
              <div className="ml-auto font-mono text-[9px] uppercase tracking-widest text-slate-400">Last {ledger.length}</div>
            </div>
            {ledger.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">No activity yet.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {ledger.map((e) => {
                  const positive = e.delta > 0;
                  return (
                    <div key={e.id} data-testid={`ledger-${e.id}`} className="px-5 py-3 flex items-start gap-3 hover:bg-slate-50 transition-colors">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                        positive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
                      }`}>
                        {positive ? "+" : "–"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-900 truncate">{e.note}</div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">
                          {e.kind.replace(/_/g, " ")} · {new Date(e.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div className={`font-mono text-sm font-bold shrink-0 ${positive ? "text-emerald-600" : "text-slate-700"}`}>
                        {positive ? "+" : ""}{e.delta}
                      </div>
                      <div className="font-mono text-[10px] text-slate-400 shrink-0 pt-1">→ {e.balance_after}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
